import React, { useState } from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { testimonials } from '../data/mock';
import { Quote, Plus, Minus, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { question: "How does Sandgen Mind actually work?", answer: "RAG-powered neural matching. In plain English: we understand context, not just keywords. While others play word bingo, we comprehend intent. The difference is night and day." },
    { question: "Can I bend the pipeline to my will?", answer: "Completely. Add stages, remove them, reorder at will. Sandgen Flow adapts to your process—not the other way around. Your hiring, your rules." },
    { question: "How paranoid are you about security?", answer: "Obsessively. SOC 2 Type II. GDPR compliant. 256-bit encryption at rest and in transit. Your data is safer here than in your own filing cabinet." },
    { question: "What&apos;s this live coding thing?", answer: "Sandgen Code: 20+ languages, real-time execution, simultaneous editing. Technical interviews that actually test technical skills. Revolutionary, we know." },
    { question: "Will it play nice with my existing tools?", answer: "Slack, Teams, Google Calendar, Outlook, LinkedIn, Greenhouse, Workday, Zoom—and a REST API for everything else. We integrate; we don&apos;t isolate." },
    { question: "Can I try before I buy?", answer: "14 days. Full access. No credit card. No sales calls unless you want them. We&apos;re confident you&apos;ll stay." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Our Story</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              We got tired of
              <br />
              <span className="text-neutral-400">mediocre hiring.</span>
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              So we built something better. Sandgen exists because exceptional companies deserve
              exceptional talent—and vice versa.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">The Verdict</span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              Don&apos;t take our <span className="text-neutral-400">word for it.</span>
            </h2>
            <p className="text-lg text-neutral-500">
              We could brag all day. But we&apos;ll let our clients do the talking.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
              className="bg-white border flex flex-col justify-between border-neutral-200 p-10 group hover:border-neutral-300 hover:shadow-lg transition-all duration-300"
              >
                <div>
                  <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mb-8 group-hover:bg-black group-hover:text-white transition-all duration-300">
                    <Quote className="w-6 h-6" />
                  </div>
                  <p className="text-neutral-700 leading-relaxed mb-10">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <h4 className="text-black font-semibold">{testimonial.author}</h4>
                    <p className="text-neutral-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 pt-12 border-t border-neutral-200">
            <p className="text-neutral-400 text-sm text-center mb-10">Trusted by 500+ organizations who refuse to settle</p>
            <div className="flex flex-wrap items-center justify-center gap-12">
              {['sitara', 'hetero', 'cgs', 'azista', 'quickstart', 'oxygem', 'iare'].map((client, i) => (
                <img
                  key={i}
                  src={`/clients/${client}.png`}
                  alt={client}
                  className="h-10 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Curiosities</span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              You asked. <span className="text-neutral-400">We answered.</span>
            </h2>
            <p className="text-lg text-neutral-500">
              The questions everyone asks—before they become believers.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-neutral-200">
                <button
                  className="w-full py-6 flex items-center justify-between text-left group"
                  onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                >
                  <span className="text-black font-medium pr-8 group-hover:text-neutral-600 transition-colors">
                    {faq.question}
                  </span>
                  <span className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-black group-hover:text-white transition-all duration-300">
                    {openIndex === index ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                  <p className="text-neutral-500 leading-relaxed pr-16">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-neutral-500">
              Still curious?{' '}
              <a href="mailto:hello@sandgen.ai" className="text-black font-medium hover:underline">We&apos;re happy to chat</a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-black relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-dark" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1] mb-8 tracking-tight">
              Your competition
              <br />
              <span className="text-neutral-500">already signed up.</span>
            </h2>
            <p className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto mb-12">
              500+ organizations have already made the switch. The only question is: how long will you wait?
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Link to="/pricing" className="bg-white text-black px-10 py-4 rounded-full font-medium text-base gap-2 hover:bg-neutral-200 transition-colors inline-flex items-center">
                Start Free Trial
                <ArrowRight size={18} />
              </Link>
              <button className="border border-neutral-600 text-white px-10 py-4 rounded-full font-medium text-base gap-2 hover:bg-white hover:text-black hover:border-white transition-all inline-flex items-center">
                <Mail size={18} />
                Contact Sales
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-neutral-500 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-white rounded-full" />
                14-day free trial
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-white rounded-full" />
                No credit card required
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-white rounded-full" />
                Cancel anytime
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
